import React, { useRef, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { SpinnerIcon } from '@/components/ui/SpinnerIcon';
// import { type AnalysisResult } from '@/app/api/analyze-sentence/schema';
import { motion, AnimatePresence } from 'framer-motion';
import { BookOpen, Lightbulb, MessageSquare, Edit3 } from 'lucide-react';

interface StructuredAnalysisDialogProps {
  isVisible: boolean;
  isAnalyzing: boolean;
  analysisObject?: any;
  onClose: () => void;
  triggerElementRef?: React.RefObject<HTMLElement>;
}

export function StructuredAnalysisDialog({
  isVisible,
  isAnalyzing,
  analysisObject,
  onClose,
  triggerElementRef,
}: StructuredAnalysisDialogProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [isPositioned, setIsPositioned] = useState(false);

  // Create portal container and calculate position
  useEffect(() => {
    if (!isVisible) {
      setIsPositioned(false);
      return;
    }

    // Create portal container
    let container = document.getElementById('analysis-dialog-portal');
    if (!container) {
      container = document.createElement('div');
      container.id = 'analysis-dialog-portal';
      container.style.position = 'fixed';
      container.style.top = '0';
      container.style.left = '0';
      container.style.width = '100%';
      container.style.height = '100%';
      container.style.pointerEvents = 'none';
      container.style.zIndex = '10000';
      document.body.appendChild(container);
    }
    setPortalContainer(container);

    // Position calculation function
    const calculatePosition = () => {
      let targetElement = null;
      
      // Method 1: Use the passed ref if available
      if (triggerElementRef?.current) {
        targetElement = triggerElementRef.current;
      }
      
      // Method 2: Look for FloatingToolbar first
      if (!targetElement) {
        const toolbar = document.querySelector('[data-floating-toolbar]');
        if (toolbar) {
          targetElement = toolbar;
        }
      }
      
      // Method 3: Look for active chunk with highlighting
      if (!targetElement) {
        const activeGroup = document.querySelector('[data-active="true"]');
        if (activeGroup) {
          // Look for chunk spans with various possible classes
          const chunkSpans = activeGroup.querySelectorAll('span[class*="cursor-pointer"]');
          
          if (chunkSpans.length > 0) {
            // Try to find the highlighted one or use the first visible one
            const highlightedChunk = Array.from(chunkSpans).find(span => 
              span.classList.contains('bg-[#FF7F50]/20') || 
              span.classList.contains('bg-primary') ||
              getComputedStyle(span).backgroundColor !== 'rgba(0, 0, 0, 0)'
            );
            targetElement = highlightedChunk || chunkSpans[0];
          }
        }
      }
      
      // Method 4: Fallback to any highlighted element
      if (!targetElement) {
        const highlighted = document.querySelector('[data-word-highlight]');
        if (highlighted) {
          targetElement = highlighted;
        }
      }
      
      if (targetElement) {
        const rect = targetElement.getBoundingClientRect();
        const newTop = rect.bottom + 16;
        const newLeft = Math.max(20, Math.min(rect.left, window.innerWidth - 520));
        
        // Only update if position actually changed (avoid unnecessary re-renders)
        setPosition(prev => {
          if (Math.abs(prev.top - newTop) > 2 || Math.abs(prev.left - newLeft) > 2) {
            return { top: newTop, left: newLeft };
          }
          return prev;
        });
        
        if (!isPositioned) {
          setIsPositioned(true);
        }
      } else {
        // Fallback position - center of screen
        const fallbackTop = 200;
        const fallbackLeft = Math.max(20, (window.innerWidth - 500) / 2);
        
        setPosition(prev => {
          if (Math.abs(prev.top - fallbackTop) > 2 || Math.abs(prev.left - fallbackLeft) > 2) {
            return { top: fallbackTop, left: fallbackLeft };
          }
          return prev;
        });
        
        if (!isPositioned) {
          setIsPositioned(true);
        }
      }
    };

    // Initial position calculation
    calculatePosition();
    const initialTimer = setTimeout(calculatePosition, 10);

    // Add scroll listener to update position when page scrolls
    const handleScroll = () => {
      if (isPositioned) {
        calculatePosition();
      }
    };

    // Add resize listener to update position when window resizes
    const handleResize = () => {
      if (isPositioned) {
        calculatePosition();
      }
    };

    // Setup event listeners
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize, { passive: true });

    // Setup ResizeObserver to track target element changes
    let resizeObserver: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        if (isPositioned) {
          calculatePosition();
        }
      });

      // Observe the active group for size/position changes
      const activeGroup = document.querySelector('[data-active="true"]');
      if (activeGroup) {
        resizeObserver.observe(activeGroup);
      }

      // Also observe the trigger element if available
      if (triggerElementRef?.current) {
        resizeObserver.observe(triggerElementRef.current);
      }
    }

    // Cleanup function
    return () => {
      clearTimeout(initialTimer);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
      
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      
      const portal = document.getElementById('analysis-dialog-portal');
      if (portal && portal.parentNode) {
        portal.parentNode.removeChild(portal);
      }
    };
  }, [isVisible, triggerElementRef, isPositioned]);

  // Auto-scroll to bottom when new content is added
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [analysisObject]);

  if (!isVisible || !portalContainer || !isPositioned) return null;

  // Check if we have structured data
  const hasStructuredData = analysisObject && Object.keys(analysisObject).length > 0;

  const dialogContent = (
    <div 
      className="absolute"
      style={{ 
        top: position.top,
        left: position.left,
        pointerEvents: 'auto',
        zIndex: 10001
      }}
    >
      <button
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        className="absolute -right-2 -top-2 p-1 rounded-full bg-background shadow-sm border border-[#8B4513]/30"
        style={{ zIndex: 10002 }}
        aria-label="Close analysis"
      >
        <svg className="h-4 w-4" fill="none" stroke="#8B4513" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
      
      <div
        className="relative border border-[#c4bcab] bg-background shadow-lg shadow-[#c4bcab]/50 rounded-xl max-w-[500px] min-w-[300px] sm:min-w-[400px] flex flex-col overflow-hidden"
        style={{ maxHeight: "400px" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 px-4 pt-1.5 pb-1.5 border-b border-[#c4bcab] flex justify-between items-center bg-primary/50">
          <h3 className="text-[16px] font-semibold text-background">AI Analysis</h3>
          {isAnalyzing && (
            <div className="flex items-center space-x-1">
              <SpinnerIcon size="sm" color="#FF7F50" />
              <span className="text-xs text-[#8f1e00]">Analyzing...</span>
            </div>
          )}
        </div>
        
        <div ref={scrollRef} className="flex-1 overflow-y-auto px-2.5 py-2.5 text-[#000000]">
          {isAnalyzing && !hasStructuredData && (
            <div className="flex items-center justify-center py-8">
              <div className="flex items-center space-x-2 text-[#2c2c2c]">
                <SpinnerIcon size="sm" color="#FF7F50" />
                <span className="text-sm">Analyzing sentence structure...</span>
              </div>
            </div>
          )}

          {hasStructuredData && (
            <div className="space-y-4">
              {/* Sentence Structure */}
              <AnimatePresence>
                {analysisObject?.sentenceStructure?.content && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-background/50 border border-border/50 rounded-xl p-3 space-y-2"
                  >
                    <div className="flex items-center space-x-2">
                      <div className="flex items-center justify-center w-6 h-6 bg-primary/10 rounded-lg flex-shrink-0">
                        <BookOpen className="h-4 w-4 text-primary" />
                      </div>
                      <h4 className="font-semibold text-sm">
                        {analysisObject.sentenceStructure.title || '句子结构'}
                      </h4>
                    </div>
                    <div className="ml-8">
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        {analysisObject.sentenceStructure.content}
                      </p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Grammar Analysis */}
              <AnimatePresence>
                {analysisObject?.grammarAnalysis?.content && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="bg-background/50 border border-border/50 rounded-xl p-3 space-y-3"
                  >
                    <div className="flex items-center space-x-2">
                      <div className="flex items-center justify-center w-6 h-6 bg-primary/10 rounded-lg flex-shrink-0">
                        <Lightbulb className="h-4 w-4 text-primary" />
                      </div>
                      <h4 className="font-semibold text-sm">
                        {analysisObject.grammarAnalysis.title || '语法分析'}
                      </h4>
                    </div>
                    <div className="ml-8">
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        {analysisObject.grammarAnalysis.content}
                      </p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Context Understanding */}
              <AnimatePresence>
                {analysisObject?.contextUnderstanding?.content && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="bg-background/50 border border-border/50 rounded-xl p-3 space-y-2"
                  >
                    <div className="flex items-center space-x-2">
                      <div className="flex items-center justify-center w-6 h-6 bg-primary/10 rounded-lg flex-shrink-0">
                        <MessageSquare className="h-4 w-4 text-primary" />
                      </div>
                      <h4 className="font-semibold text-sm">
                        {analysisObject.contextUnderstanding.title || '语境理解'}
                      </h4>
                    </div>
                    <div className="ml-8">
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        {analysisObject.contextUnderstanding.content}
                      </p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Possible Rewrites */}
              <AnimatePresence>
                {analysisObject?.possibleRewrites?.suggestions && analysisObject.possibleRewrites.suggestions.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="bg-background/50 border border-border/50 rounded-xl p-3 space-y-2"
                  >
                    <div className="flex items-center space-x-2">
                      <div className="flex items-center justify-center w-6 h-6 bg-primary/10 rounded-lg flex-shrink-0">
                        <Edit3 className="h-4 w-4 text-primary" />
                      </div>
                      <h4 className="font-semibold text-sm">
                        {analysisObject.possibleRewrites.title || '可能的改写'}
                      </h4>
                    </div>
                    <div className="ml-8 space-y-3">
                      {analysisObject.possibleRewrites.suggestions.map((suggestion: any, index: number) => (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.4 + index * 0.1 }}
                          className="border rounded-lg p-3 space-y-2 bg-primary/5 border-primary/20"
                        >
                          <div className="flex items-start gap-2">
                            <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary flex-shrink-0 mt-0.5">
                              {index + 1}
                            </span>
                            <div className="flex-1 min-w-0">
                              {(suggestion?.rewrite || suggestion?.text) && (
                                <p className="text-sm text-foreground leading-relaxed break-words">
                                  &ldquo;{suggestion?.rewrite || suggestion?.text}&rdquo;
                                </p>
                              )}
                            </div>
                          </div>
                          {suggestion?.explanation && (
                            <p className="text-xs text-muted-foreground leading-relaxed border-l-2 border-primary/30 pl-3 break-words">
                              {suggestion.explanation}
                            </p>
                          )}
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(dialogContent, portalContainer);
}
